import { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import TaskSkeleton from './TaskSkeleton';
import Select from 'react-select';

export default function AntivirusRequestList() {
    const { http, user } = AuthUser();
    const [requests, setRequests] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [customers, setCustomers] = useState([]); // For company selection
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null); // For company dropdown
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTypeRequest, setFilterTypeRequest] = useState(''); // Filter by type request
    const [filterStatus, setFilterStatus] = useState(''); // Filter by expiry status
    
    // Super Admin only (same as Company menu / antivirus admin checks)
    const isSuperAdmin = user && (user.id == 1 || user.id == 3);
    // Admin users for listing filters (kept for My Requests toggle)
    const isAdminUser = isSuperAdmin;
    
    // For non-admin users, always show only their requests; for admins, default to false (show all)
    const [showMyRequestsOnly, setShowMyRequestsOnly] = useState(!isAdminUser);
    const [formData, setFormData] = useState({
        type_request: '',
        product: '',
        company_name: '',
        username: '',
        contact_num: '',
        price: '',
        antivirus_activated_on: '',
        remark: ''
    });

    useEffect(() => {
        fetchRequests();
        fetchAvailableProducts();
        fetchCustomers();
    }, []);

    const fetchRequests = () => {
        setIsLoading(true);
        http.get('/antivirus_request').then((res) => {
            setIsLoading(false);
            if (res.data.success && Array.isArray(res.data.data)) {
                setRequests(res.data.data);
            } else {
                console.error("API response error:", res.data);
                toast.error("Failed to fetch antivirus requests");
            }
        }).catch(error => {
            setIsLoading(false);
            console.error("Error fetching antivirus requests:", error);
            toast.error("Error fetching antivirus requests");
        });
    };

    const fetchAvailableProducts = () => {
        http.get('/antivirus_request/available-products').then((res) => {
            if (res.data.success && Array.isArray(res.data.data)) {
                setAvailableProducts(res.data.data);
            }
        }).catch(error => {
            console.error("Error fetching available products:", error);
        });
    };

    const fetchCustomers = () => {
        http.get('/get_customer_dropdown').then((res) => {
            if (res.data.customers && Array.isArray(res.data.customers)) {
                setCustomers(res.data.customers);
            }
        }).catch(error => {
            console.error("Error fetching customers:", error);
        });
    };

    // Create options for company dropdown
    const companyOptions = customers.map((customer) => ({
        value: customer.company,
        label: `${customer.company}${customer.phone ? ' - ' + customer.phone : ''}`,
        phone: customer.phone,
    }));

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // If changing type_request, clear the opposite field
        if (name === 'type_request') {
            if (value === 'Company') {
                // Clear username when switching to Company
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    username: ''
                }));
                // Keep selectedCompany as is
            } else if (value === 'Personal') {
                // Clear company_name when switching to Personal
                setFormData(prev => ({
                    ...prev,
                    [name]: value,
                    company_name: ''
                }));
                setSelectedCompany(null); // Clear company dropdown
            } else {
                // If empty/no selection, keep both
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
                setSelectedCompany(null); // Clear company dropdown
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.product) {
            toast.error("Please select a product");
            return;
        }
        
        setIsLoading(true);

        // Add created_by with current user's ID
        const submitData = {
            ...formData,
            created_by: user.id
        };

        try {
            let response;
            if (editingRequest) {
                if (!isSuperAdmin) {
                    toast.error("Only Super Admin can edit");
                    setIsLoading(false);
                    return;
                }
                response = await http.post(`/antivirus_request/${editingRequest.id}`, {
                    _method: 'PUT',
                    ...submitData
                });
                toast.success("Antivirus request updated successfully!");
            } else {
                response = await http.post('/antivirus_request', submitData);
                toast.success("Antivirus request created successfully!");
            }

            if (response.data.success) {
                setShowModal(false);
                setFormData({
                    type_request: '',
                    product: '',
                    company_name: '',
                    username: '',
                    contact_num: '',
                    price: '',
                    antivirus_activated_on: '',
                    remark: ''
                });
                setEditingRequest(null);
                setSelectedProduct(null);
                setSelectedCompany(null);
                fetchRequests();
                fetchAvailableProducts();
            }
        } catch (error) {
            console.error("Error saving antivirus request:", error);
            const errorMessage = error.response?.data?.message || "Error saving antivirus request";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (request) => {
        if (!isSuperAdmin) {
            toast.error("Only Super Admin can edit");
            return;
        }
        setEditingRequest(request);
        setFormData({
            type_request: request.type_request || '',
            product: request.product || '',
            company_name: request.company_name || '',
            username: request.username || '',
            contact_num: request.contact_num || '',
            price: request.price || '',
            antivirus_activated_on: formatDateForInput(request.antivirus_activated_on) || '',
            remark: request.remark || ''
        });
        
        // Set the selected product for react-select
        if (request.antivirus_master) {
            setSelectedProduct({
                value: request.product,
                label: `${request.antivirus_master.type_name} - ${request.antivirus_master.type_year} (${request.antivirus_master.key})`
            });
        }
        
        // Set the selected company for react-select if type_request is Company
        if (request.type_request === 'Company' && request.company_name) {
            // Find the customer with matching company name
            const matchingCustomer = customers.find(c => c.company === request.company_name);
            if (matchingCustomer) {
                setSelectedCompany({
                    value: matchingCustomer.company,
                    label: `${matchingCustomer.company}${matchingCustomer.phone ? ' - ' + matchingCustomer.phone : ''}`
                });
            } else {
                setSelectedCompany({
                    value: request.company_name,
                    label: request.company_name
                });
            }
        } else {
            setSelectedCompany(null);
        }
        
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!isSuperAdmin) {
            toast.error("Only Super Admin can delete");
            return;
        }
        if (window.confirm("Are you sure you want to delete this antivirus request?")) {
            try {
                const response = await http.delete(`/antivirus_request/${id}`);
                if (response.data.success) {
                    toast.success("Antivirus request deleted successfully!");
                    fetchRequests();
                    fetchAvailableProducts();
                }
            } catch (error) {
                console.error("Error deleting antivirus request:", error);
                toast.error("Error deleting antivirus request");
            }
        }
    };

    const resetForm = () => {
        setFormData({
            type_request: '',
            product: '',
            company_name: '',
            username: '',
            contact_num: '',
            price: '',
            antivirus_activated_on: '',
            remark: ''
        });
        setEditingRequest(null);
        setSelectedProduct(null);
        setSelectedCompany(null);
        setShowModal(false);
    };

    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
        setFormData(prev => ({
            ...prev,
            product: selectedOption ? selectedOption.value : ''
        }));
    };

    // Convert products to react-select format
    const productOptions = availableProducts.map(product => ({
        value: product.id,
        label: `${product.type_name} - ${product.type_year}`
    }));

    // Calculate expiry date and days left
    const calculateExpiryInfo = (activatedOn, typeYear) => {
        if (!activatedOn || !typeYear) {
            return { expireOn: null, daysLeft: null };
        }

        const years = parseInt(typeYear.match(/\d+/)?.[0]) || 0;
        const expireOn = moment(activatedOn).add(years, 'years');
        const daysLeft = expireOn.diff(moment(), 'days');

        return { expireOn, daysLeft };
    };

    return (
        <div className="container-fluid">
            <Toaster />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="card-title mb-0">Antivirus Requests</h3>
                                {!isAdminUser && (
                                    <small className="text-muted">
                                        <i className="fas fa-info-circle me-1"></i>
                                        Viewing your requests only
                                    </small>
                                )}
                            </div>
                            <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                    setFormData({
                                        type_request: '',
                                        product: '',
                                        company_name: '',
                                        username: '',
                                        contact_num: '',
                                        price: '',
                                        antivirus_activated_on: '',
                                        remark: ''
                                    });
                                    setEditingRequest(null);
                                    setSelectedProduct(null);
                                    setSelectedCompany(null);
                                    setShowModal(true);
                                }}
                            >
                                <i className="fas fa-plus-circle me-1"></i> Add Request
                            </button>
                        </div>
                        <div className="card-body">
                            {/* Filter Section */}
                            <div className="row mb-3">
                                <div className={isAdminUser ? "col-md-3" : "col-md-4"}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by company, username, contact, or staff name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className={isAdminUser ? "col-md-3" : "col-md-4"}>
                                    <select
                                        className="form-select"
                                        value={filterTypeRequest}
                                        onChange={(e) => setFilterTypeRequest(e.target.value)}
                                    >
                                        <option value="">All Request Types</option>
                                        <option value="Company">Company</option>
                                        <option value="Personal">Personal</option>
                                    </select>
                                </div>
                                <div className={isAdminUser ? "col-md-3" : "col-md-4"}>
                                    <select
                                        className="form-select"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="">All Status</option>
                                        <option value="active">Active (Not Expired)</option>
                                        <option value="expiring_soon">Expiring Soon (&lt; 30 days)</option>
                                        <option value="expired">Expired</option>
                                    </select>
                                </div>
                                {/* Only show toggle button for admin users (ID 1 or 3) */}
                                {isAdminUser && (
                                    <div className="col-md-3">
                                        <button
                                            className={`btn w-100 ${showMyRequestsOnly ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setShowMyRequestsOnly(!showMyRequestsOnly)}
                                        >
                                            <i className={`fas ${showMyRequestsOnly ? 'fa-user-check' : 'fa-users'} me-2`}></i>
                                            {showMyRequestsOnly ? 'My Requests' : 'All Requests'}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {isLoading ? (
                                <TaskSkeleton />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Type Request</th>
                                                <th>Product</th>
                                                <th>Key</th>
                                                <th>Company Name</th>
                                                <th>Username</th>
                                                <th>Contact Number</th>
                                                <th>Price</th>
                                                <th>Activated On</th>
                                                <th>Expire On</th>
                                                <th>Days Left</th>
                                                <th>Requested Staff</th>
                                                <th>Created At</th>
                                                {isSuperAdmin && <th>Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {requests.filter(request => {
                                                const searchLower = searchTerm.toLowerCase();
                                                const staffName = request.created_by_user 
                                                    ? `${request.created_by_user.name} ${request.created_by_user.last_name}`.toLowerCase()
                                                    : '';
                                                
                                                // Search filter
                                                const matchesSearch = searchTerm === '' || (
                                                    (request.company_name || '').toLowerCase().includes(searchLower) ||
                                                    (request.username || '').toLowerCase().includes(searchLower) ||
                                                    (request.contact_num || '').toLowerCase().includes(searchLower) ||
                                                    staffName.includes(searchLower) ||
                                                    (request.antivirus_master?.type_name || '').toLowerCase().includes(searchLower)
                                                );
                                                
                                                // Type Request filter
                                                const matchesTypeRequest = filterTypeRequest === '' || 
                                                    request.type_request === filterTypeRequest;
                                                
                                                // Status filter (based on expiry)
                                                const { daysLeft } = calculateExpiryInfo(
                                                    request.antivirus_activated_on,
                                                    request.antivirus_master?.type_year
                                                );
                                                const matchesStatus = filterStatus === '' ||
                                                    (filterStatus === 'active' && daysLeft !== null && daysLeft >= 30) ||
                                                    (filterStatus === 'expiring_soon' && daysLeft !== null && daysLeft >= 0 && daysLeft < 30) ||
                                                    (filterStatus === 'expired' && daysLeft !== null && daysLeft < 0);
                                                
                                                // Created by filter
                                                const matchesCreatedBy = !showMyRequestsOnly || request.created_by === user.id;
                                                
                                                return matchesSearch && matchesTypeRequest && matchesStatus && matchesCreatedBy;
                                            }).length > 0 ? (
                                                requests.filter(request => {
                                                    const searchLower = searchTerm.toLowerCase();
                                                    const staffName = request.created_by_user 
                                                        ? `${request.created_by_user.name} ${request.created_by_user.last_name}`.toLowerCase()
                                                        : '';
                                                    
                                                    // Search filter
                                                    const matchesSearch = searchTerm === '' || (
                                                        (request.company_name || '').toLowerCase().includes(searchLower) ||
                                                        (request.username || '').toLowerCase().includes(searchLower) ||
                                                        (request.contact_num || '').toLowerCase().includes(searchLower) ||
                                                        staffName.includes(searchLower) ||
                                                        (request.antivirus_master?.type_name || '').toLowerCase().includes(searchLower)
                                                    );
                                                    
                                                    // Type Request filter
                                                    const matchesTypeRequest = filterTypeRequest === '' || 
                                                        request.type_request === filterTypeRequest;
                                                    
                                                    // Status filter (based on expiry)
                                                    const { daysLeft } = calculateExpiryInfo(
                                                        request.antivirus_activated_on,
                                                        request.antivirus_master?.type_year
                                                    );
                                                    const matchesStatus = filterStatus === '' ||
                                                        (filterStatus === 'active' && daysLeft !== null && daysLeft >= 30) ||
                                                        (filterStatus === 'expiring_soon' && daysLeft !== null && daysLeft >= 0 && daysLeft < 30) ||
                                                        (filterStatus === 'expired' && daysLeft !== null && daysLeft < 0);
                                                    
                                                    // Created by filter
                                                    const matchesCreatedBy = !showMyRequestsOnly || request.created_by === user.id;
                                                    
                                                    return matchesSearch && matchesTypeRequest && matchesStatus && matchesCreatedBy;
                                                }).map((request) => (
                                                    <tr key={request.id}>
                                                        <td>{request.id}</td>
                                                        <td>{request.type_request || '-'}</td>
                                                        <td>
                                                            {request.antivirus_master 
                                                                ? (
                                                                    <div>
                                                                        <strong>{request.antivirus_master.type_name}</strong>
                                                                        <br />
                                                                        <small className="text-muted">{request.antivirus_master.type_year}</small>
                                                                    </div>
                                                                )
                                                                : '-'
                                                            }
                                                        </td>
                                                        <td>
                                                            {request.antivirus_master && request.antivirus_master.key
                                                                ? (
                                                                    <div style={{ 
                                                                        backgroundColor: '#f8f9fa', 
                                                                        padding: '6px 10px', 
                                                                        borderRadius: '4px',
                                                                        border: '1px solid #dee2e6',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: '11px',
                                                                        wordBreak: 'break-word',
                                                                        maxWidth: '150px',
                                                                        lineHeight: '1.4'
                                                                    }}>
                                                                        <div style={{ marginBottom: '2px' }}>
                                                                            <i className="fas fa-key me-1" style={{ color: '#6c757d', fontSize: '10px' }}></i>
                                                                        </div>
                                                                        <div>{request.antivirus_master.key}</div>
                                                                    </div>
                                                                )
                                                                : '-'
                                                            }
                                                        </td>
                                                        <td>{request.company_name || '-'}</td>
                                                        <td>{request.username || '-'}</td>
                                                        <td>{request.contact_num || '-'}</td>
                                                        <td>{request.price || '-'}</td>
                                                        <td>{request.antivirus_activated_on ? moment(request.antivirus_activated_on).format('DD/MM/YYYY') : '-'}</td>
                                                        <td>
                                                            {(() => {
                                                                const { expireOn } = calculateExpiryInfo(
                                                                    request.antivirus_activated_on,
                                                                    request.antivirus_master?.type_year
                                                                );
                                                                return expireOn ? moment(expireOn).format('DD/MM/YYYY') : '-';
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const { daysLeft } = calculateExpiryInfo(
                                                                    request.antivirus_activated_on,
                                                                    request.antivirus_master?.type_year
                                                                );
                                                                if (daysLeft === null) return '-';
                                                                
                                                                const badgeClass = daysLeft < 0 
                                                                    ? 'bg-danger' 
                                                                    : daysLeft < 30 
                                                                        ? 'bg-warning' 
                                                                        : 'bg-success';
                                                                
                                                                return (
                                                                    <span className={`badge ${badgeClass}`} style={{ fontSize: '12px' }}>
                                                                        {daysLeft < 0 
                                                                            ? `Expired ${Math.abs(daysLeft)} days ago` 
                                                                            : `${daysLeft} days`
                                                                        }
                                                                    </span>
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {request.created_by_user 
                                                                ? (
                                                                    <div>
                                                                        <span className="badge bg-info" style={{ fontSize: '12px' }}>
                                                                            <i className="fas fa-user me-1"></i>
                                                                            {request.created_by_user.name} {request.created_by_user.last_name}
                                                                        </span>
                                                                    </div>
                                                                )
                                                                : '-'
                                                            }
                                                        </td>
                                                        <td>{moment(request.created_at).format('DD/MM/YYYY HH:mm')}</td>
                                                        {isSuperAdmin && (
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            <button 
                                                                className="btn btn-sm btn-warning me-1"
                                                                onClick={() => handleEdit(request)}
                                                                style={{ fontSize: '11px', padding: '5px 10px' }}
                                                                title="Edit"
                                                            >
                                                                <i className="fas fa-pen"></i>
                                                            </button>
                                                            <button 
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDelete(request.id)}
                                                                style={{ fontSize: '11px', padding: '5px 10px' }}
                                                                title="Delete"
                                                            >
                                                                <i className="fas fa-trash-alt"></i>
                                                            </button>
                                                        </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={isSuperAdmin ? 14 : 13} className="text-center">No antivirus requests found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingRequest ? 'Edit Antivirus Request' : 'Add New Antivirus Request'}
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={resetForm}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Type Request</label>
                                                <select
                                                    className="form-control"
                                                    name="type_request"
                                                    value={formData.type_request}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Type</option>
                                                    <option value="Company">Company</option>
                                                    <option value="Personal">Personal</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Product *</label>
                                                <Select
                                                    options={productOptions}
                                                    value={selectedProduct}
                                                    onChange={handleProductChange}
                                                    placeholder="Search and select product..."
                                                    isClearable
                                                    isSearchable
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            minHeight: '38px'
                                                        })
                                                    }}
                                                />
                                                <small className="text-muted">Only unused products are shown</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        {/* Show Company Name only if Type Request is NOT Personal */}
                                        {formData.type_request !== 'Personal' && (
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Company Name
                                                        {formData.type_request === 'Company' && <span className="text-danger"> *</span>}
                                                    </label>
                                                    <Select
                                                        options={companyOptions}
                                                        value={selectedCompany}
                                                        onChange={(selectedOption) => {
                                                            setSelectedCompany(selectedOption);
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                company_name: selectedOption ? selectedOption.value : '',
                                                                contact_num: selectedOption ? selectedOption.phone : ''
                                                            }));
                                                        }}
                                                        placeholder="Select a company..."
                                                        isClearable
                                                        isSearchable
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {/* Show Username only if Type Request is NOT Company */}
                                        {formData.type_request !== 'Company' && (
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Username
                                                        {formData.type_request === 'Personal' && <span className="text-danger"> *</span>}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="username"
                                                        value={formData.username}
                                                        onChange={handleInputChange}
                                                        required={formData.type_request === 'Personal'}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Contact Number</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="contact_num"
                                                    value={formData.contact_num}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label className="form-label">Price</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="price"
                                                    value={formData.price}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">Antivirus Activated On</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="antivirus_activated_on"
                                                    value={formData.antivirus_activated_on}
                                                    onChange={handleInputChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="mb-3">
                                                <label className="form-label">Remark</label>
                                                <textarea
                                                    className="form-control"
                                                    name="remark"
                                                    rows={2}
                                                    value={formData.remark}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter remark (optional)"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Saving...' : (editingRequest ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

